/**
 * /cricket/dedupe — find + merge duplicate players and teams.
 *
 * Backend:
 *   GET  /api/admin/dedupe/?type=players  → [{key, candidates: [{id, name, ...}]}]
 *   POST /api/admin/dedupe/merge/ { type, keeper_id, dupe_ids } → {moved, deleted}
 *
 * Office-team only. Reads admin auth from session.
 */
import { useEffect, useRef, useState } from "react";
import { Users, Shield, Merge, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import api from "../../api/axios";
import useDialogDismiss from "../../hooks/useDialogDismiss";

function Group({ group, kind, onMerged }) {
  const [keeper, setKeeper] = useState(group.candidates[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const panelRef = useRef(null);
  useDialogDismiss({
    open: previewOpen,
    onClose: () => !busy && setPreviewOpen(false),
    ref: panelRef,
  });

  const dupes = group.candidates.filter((c) => c.id !== keeper);
  const dupeIds = dupes.map((c) => c.id);
  const keeperRow = group.candidates.find((c) => c.id === keeper);
  const totalRefs = dupes.reduce((acc, c) => acc + (c.ref_count || 0), 0);

  const doMerge = async () => {
    if (!dupeIds.length) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.post("/api/admin/dedupe/merge/", { type: kind, keeper_id: keeper, dupe_ids: dupeIds });
      setMsg(`Merged. Moved ${r.data.moved} FK references, deleted ${r.data.deleted} record(s).`);
      setPreviewOpen(false);
      onMerged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Merge failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-bg-primary border border-border-default rounded-xl p-4 mb-3">
      <div className="text-xs uppercase tracking-wider text-text-secondary mb-2">
        Group key: <span className="text-text-primary font-mono">{group.key}</span>
      </div>
      <table className="w-full text-sm mb-3">
        <thead className="text-xs text-text-secondary">
          <tr>
            <th className="text-left py-1.5 w-8">Keep</th>
            <th className="text-left py-1.5">Name</th>
            <th className="text-left py-1.5">ID</th>
            <th className="text-left py-1.5">Refs</th>
          </tr>
        </thead>
        <tbody>
          {group.candidates.map((c) => (
            <tr key={c.id} className="border-t border-border-default">
              <td className="py-1.5">
                <input type="radio" name={`keeper-${group.key}`} checked={keeper === c.id}
                       onChange={() => setKeeper(c.id)} />
              </td>
              <td className="py-1.5">{c.name}</td>
              <td className="py-1.5 font-mono text-[10px] text-text-secondary">{c.id.slice(0, 8)}…</td>
              <td className="py-1.5 text-text-secondary">{c.ref_count ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {msg && <div className="text-xs text-emerald-400 mb-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />{msg}</div>}
      {err && <div className="text-xs text-button-primary mb-2 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{err}</div>}
      <button
        onClick={() => setPreviewOpen(true)}
        disabled={busy || dupeIds.length === 0}
        className="bg-button-primary hover:brightness-110 disabled:opacity-50 text-white text-sm font-semibold py-2 px-3 rounded-lg flex items-center gap-1.5"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />}
        Review &amp; merge
      </button>

      {previewOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setPreviewOpen(false); }}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Confirm merge"
            className="bg-bg-primary border border-border-default rounded-xl shadow-2xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
              <Merge className="w-4 h-4" /> Confirm merge
            </h3>
            <p className="text-sm text-text-secondary mb-3">
              This will permanently delete the duplicate records and move every
              foreign-key reference to the keeper. Irreversible.
            </p>
            <div className="rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 mb-2 text-xs">
              <span className="text-emerald-300 font-semibold">Keep: </span>
              <span className="text-text-primary">{keeperRow?.name}</span>
              <span className="font-mono text-text-secondary ml-2">{keeperRow?.id?.slice(0, 8)}…</span>
            </div>
            <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 mb-3 text-xs">
              <div className="text-rose-300 font-semibold mb-1">Delete {dupes.length} record{dupes.length === 1 ? "" : "s"}:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {dupes.map((d) => (
                  <li key={d.id}>
                    {d.name} <span className="font-mono text-text-secondary">{d.id?.slice(0, 8)}…</span>
                    {d.ref_count != null && <span className="text-text-secondary ml-1">· {d.ref_count} ref{d.ref_count === 1 ? "" : "s"}</span>}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-[11px] text-text-secondary mb-3">
              ~{totalRefs} foreign-key reference{totalRefs === 1 ? "" : "s"} will move to the keeper.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPreviewOpen(false)} disabled={busy} className="px-3 py-1.5 rounded bg-bg-secondary text-text-secondary text-sm">Cancel</button>
              <button onClick={doMerge} disabled={busy} className="px-3 py-1.5 rounded bg-rose-600 text-white text-sm">
                {busy ? "Merging…" : `Merge ${dupes.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dedupe() {
  const [tab, setTab] = useState("players");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const reload = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/api/admin/dedupe/?type=${tab}`);
      setGroups(r.data?.groups || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load duplicates.");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [tab]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-xl font-bold mb-1">De-dupe tool</h2>
      <p className="text-sm text-text-secondary mb-4">
        Find duplicate players and teams (often created by bulk imports), then merge while preserving foreign-key references.
      </p>

      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setTab("players")}
          className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${tab === "players" ? "bg-button-primary text-white" : "bg-bg-secondary text-text-secondary"}`}
        >
          <Users className="w-4 h-4" /> Players
        </button>
        <button
          onClick={() => setTab("teams")}
          className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${tab === "teams" ? "bg-button-primary text-white" : "bg-bg-secondary text-text-secondary"}`}
        >
          <Shield className="w-4 h-4" /> Teams
        </button>
      </div>

      {loading ? (
        <div className="text-text-secondary text-sm">Scanning…</div>
      ) : err ? (
        <div className="bg-button-primary/10 border border-button-primary/30 rounded-lg px-3.5 py-2.5 text-sm text-button-primary">{err}</div>
      ) : groups.length === 0 ? (
        <div className="text-sm text-emerald-400 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> No duplicates found.
        </div>
      ) : (
        groups.map((g) => <Group key={g.key} group={g} kind={tab} onMerged={reload} />)
      )}
    </div>
  );
}
